/**
 * LeaderBoardV2 - Lab Leaderboard with modern design
 * Features:
 * - Summary statistics cards (Total Students, Highest Score, Average Score, Lowest Score)
 * - Dynamic activity columns based on program_titles from API
 * - Clean table design with hover effects
 * - Auto-refresh every 15 seconds
 */
import React, { useEffect, useState, useMemo } from 'react';
import httpService from 'services/httpService';
import { useParams, Link } from 'react-router-dom';
import * as constants from 'Constants';
import { Breadcrumb } from 'react-bootstrap';
import { FiUsers } from 'react-icons/fi';
import { BsTrophy } from 'react-icons/bs';
import { TbChartBar } from 'react-icons/tb';
import { BiTrendingDown } from 'react-icons/bi';
import styles from './LeaderBoardV2.module.css';

const StatCard = ({ label, value, icon: Icon, colorClass }) => (
  <div className={styles.statCard}>
    <div className={styles.statCardContent}>
      <span className={styles.statCardLabel}>{label}</span>
      <span className={`${styles.statCardValue} ${styles[colorClass]}`}>{value}</span>
    </div>
    <div className={`${styles.statCardIcon} ${styles[colorClass + 'Bg']}`}>
      <Icon size={24} />
    </div>
  </div>
);

const LeaderBoardV2 = () => {
  const { courseId, labId } = useParams();
  const [labTitle, setLabTitle] = useState('Lab');
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [programTitles, setProgramTitles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const url = constants.base_url + `/lab/${labId}/leaderboard`;
      try {
        const response = await httpService.get(url);
        setLeaderboardData(response.data.leaderboard);
        setProgramTitles(response.data.program_titles);
        setLabTitle(response.data.lab_title);
      } catch (error) {
        console.error('Error fetching leaderboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(() => {
      fetchData();
    }, 15000);

    return () => {
      clearInterval(interval);
    };
  }, [labId]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!leaderboardData.length) {
      return { totalStudents: 0, highestScore: 0, averageScore: 0, lowestScore: 0 };
    }

    const scores = leaderboardData.map(student => student.total_score || 0);
    const totalStudents = leaderboardData.length;
    const highestScore = Math.max(...scores);
    const lowestScore = Math.min(...scores);
    const averageScore = Math.round(scores.reduce((a, b) => a + b, 0) / totalStudents);

    return { totalStudents, highestScore, averageScore, lowestScore };
  }, [leaderboardData]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <div className={styles.spinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <Breadcrumb className={styles.breadcrumb}>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs` }}>
          Labs
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs/${labId}/` }}>
          {labTitle || 'Lab'}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Leaderboard</Breadcrumb.Item>
      </Breadcrumb>

      {/* Title */}
      <h1 className={styles.pageTitle}>{labTitle} Leaderboard</h1>

      {/* Statistics Cards */}
      <div className={styles.statsRow}>
        <StatCard
          label="Total Students"
          value={stats.totalStudents}
          icon={FiUsers}
          colorClass="blue"
        />
        <StatCard
          label="Highest Score"
          value={stats.highestScore}
          icon={BsTrophy}
          colorClass="green"
        />
        <StatCard
          label="Average Score"
          value={stats.averageScore}
          icon={TbChartBar}
          colorClass="blue"
        />
        <StatCard
          label="Lowest Score"
          value={stats.lowestScore}
          icon={BiTrendingDown}
          colorClass="red"
        />
      </div>

      {/* Leaderboard Table */}
      <div className={styles.tableContainer}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.rankCol}>Rank</th>
                <th className={styles.rollCol}>Roll Number</th>
                <th className={styles.nameCol}>Student Name</th>
                {programTitles.map((title, index) => (
                  <th key={index} className={styles.questionCol}>
                    {title}
                  </th>
                ))}
                <th className={styles.totalCol}>Total Marks</th>
              </tr>
            </thead>
            <tbody>
              {leaderboardData.length === 0 ? (
                <tr>
                  <td colSpan={4 + programTitles.length} className={styles.emptyState}>
                    No students found
                  </td>
                </tr>
              ) : (
                leaderboardData.map((student, index) => (
                  <tr key={student.id || index} className={styles.tableRow}>
                    <td className={styles.rankCell}>{index + 1}</td>
                    <td className={styles.rollCell}>{student.roll_number || '-'}</td>
                    <td className={styles.nameCell}>{student.name}</td>
                    {programTitles.map((title, i) => (
                      <td key={i} className={styles.questionCell}>
                        {student[title] || 0}
                      </td>
                    ))}
                    <td className={styles.totalCell}>{student.total_score || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LeaderBoardV2;
